import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { fetchShotThumbBlob, money, pictureUrl } from "./orderDocumentApi";
import { STONE_FONT_WIDTH, SpacedLine, ensureStoneFont, epitaphChars, italicOf, lineStyle, typoOf } from "./stoneFonts";

/*
 * Мгновенный предпросмотр бланка: лист А4 нарисован прямо на странице
 * (в пунктах PDF, теми же размерами и формулами, что utils/orderDocumentPdf.js).
 * Нажал стрелку или ввёл текст — лист меняется сразу, без запроса на сервер.
 * Настоящий PDF — по кнопке «Открыть PDF» (печать, подпись).
 */
const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN_X = 28;
const BOX_W = 251;
const BOX_H = 250;
const MUTED = "#6b7280";

const CHECK_LABELS = [
    ["deliveryCemetery", "Доставка до кладбища"],
    ["deliveryGrave", "Доставка до могилы"],
    ["dismantleCross", "Демонтаж креста, опалубки"],
    ["leveling", "Выравнивание"],
    ["dismantleFormwork", "Демонтаж цементной опалубки"],
    ["channel", "Швеллер"],
];

const OPTION_LABELS = [
    ["font", "Шрифт №"],
    ["portrait", "Портрет"],
    ["ceramic", "Керамогранит"],
    ["niche", "Ниша"],
    ["symbol", "Символ / изображение"],
    ["extraText", "Дополнительный текст"],
];

const S = (value) => String(value == null ? "" : value);
const lines = (value) => S(value).split("\n").map((line) => line.trim()).filter(Boolean);
const fioLines = (person = {}) => [person.surname, person.name, person.patronymic].map((value) => S(value).trim()).filter(Boolean);
const dateText = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
};
const monthYear = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("ru-RU", { month: "long", year: "numeric" });
};

/**
 * Автоснимок сцены → камень целиком: белые поля до пропорций клетки
 * (так же делает сервер для PDF), чтобы обрезка клетки ничего не отрезала.
 */
const padToCell = (src) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        const aspect = BOX_W / (BOX_H - 6);
        const w = Math.max(img.naturalWidth, Math.round(img.naturalHeight * aspect));
        const h = Math.max(img.naturalHeight, Math.round(img.naturalWidth / aspect));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const context = canvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, w, h);
        context.drawImage(img, Math.floor((w - img.naturalWidth) / 2), Math.floor((h - img.naturalHeight) / 2));
        resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.onerror = () => resolve("");
    img.src = src;
});

/** Картинка стороны + её настоящий размер (без мигания: грузим один раз на адрес). */
const useSideImage = (officeId, doc, ref) => {
    const [state, setState] = useState({ src: "", w: 0, h: 0 });
    const refType = ref?.type || "none";
    const refId = String(ref?.id || "");
    useEffect(() => {
        let alive = true;
        let objectUrl = "";
        const load = (src) => {
            const img = new Image();
            img.onload = () => alive && setState({ src, w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = () => alive && setState({ src: "", w: 0, h: 0 });
            img.src = src;
        };
        if (refType === "scene") {
            // Автоснимок перед/зад из 3D-калькулятора (только бланк до заказа).
            const shot = doc.sceneShots?.[refId];
            if (shot?.src) {
                padToCell(shot.src).then((padded) => {
                    if (!alive) return;
                    if (padded) load(padded);
                    else setState({ src: "", w: 0, h: 0 });
                });
            } else {
                setState({ src: "", w: 0, h: 0 });
            }
        } else if (refType === "product") {
            const item = doc.items.find((entry) => String(entry.n) === refId);
            if (item?.picture) load(pictureUrl(item.picture));
            else setState({ src: "", w: 0, h: 0 });
        } else if (refType === "shot") {
            const shot = doc.shots.find((entry) => String(entry.id) === refId);
            if (shot) {
                fetchShotThumbBlob(officeId, shot.sessionCode, shot.id)
                    .then((blob) => {
                        if (!alive) return;
                        objectUrl = URL.createObjectURL(blob);
                        load(objectUrl);
                    })
                    .catch(() => undefined);
            }
        } else {
            setState({ src: "", w: 0, h: 0 });
        }
        return () => {
            alive = false;
            if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        };
    }, [officeId, doc, refType, refId]);
    return state;
};

/** Обрезка как на сервере: окно в пропорциях клетки, точка фокуса photoX/photoY. */
const cropOf = (W, H, aspect, side) => {
    const zoom = Math.max(1, Number(side.photoZoom) || 1);
    let w = W / zoom;
    let h = w / aspect;
    if (h > H) {
        h = H;
        w = H * aspect;
    }
    const cx = ((side.photoX ?? 50) / 100) * W;
    const cy = ((side.photoY ?? 50) / 100) * H;
    return {
        left: Math.min(W - w, Math.max(0, cx - w / 2)),
        top: Math.min(H - h, Math.max(0, cy - h / 2)),
        w,
        h,
    };
};

/** Фото, обрезанное и вписанное в клетку. */
function CroppedPhoto({ image, side, maxW, maxH, children }) {
    if (!image.src || !image.w) return null;
    const crop = cropOf(image.w, image.h, maxW / maxH, side);
    const fit = Math.min(maxW / crop.w, maxH / crop.h);
    const shrink = Math.min(1, Math.max(0.3, Number(side.photoZoom) || 1));
    const width = Math.round(crop.w * fit * shrink);
    const height = Math.round(crop.h * fit * shrink);
    const k = width / crop.w;
    return (
        <div className="blank-sheet__photo" style={{ width, height }}>
            <img src={image.src} alt="" style={{ width: image.w * k, height: image.h * k, left: -crop.left * k, top: -crop.top * k }} />
            {typeof children === "function" ? children({ width, height }) : null}
        </div>
    );
}

/** Шрифт надписи: уменьшаем, чтобы самая длинная строка влезла в колонку (как в PDF). */
const personScaleOf = (persons, scale, colWidth, fontWidth = 1, typo = typoOf({})) => {
    let personScale = scale * (persons.length === 3 ? 0.62 : persons.length === 2 ? 0.78 : 1);
    persons.forEach((person) => {
        fioLines(person).forEach((line, index) => {
            const per = (index === 0 && person.surname ? typo.surname * 0.8 : typo.name * 0.62) * fontWidth + typo.fioLetter;
            personScale = Math.min(personScale, (colWidth - 4) / Math.max(1, line.length * per));
        });
        lines(person?.dates).forEach((line) => {
            const per = typo.dates * 0.6 * fontWidth + typo.datesLetter;
            personScale = Math.min(personScale, (colWidth - 2) / Math.max(1, line.length * per));
        });
    });
    return personScale;
};

function PersonText({ person, scale, color, fontFamily, fontKey = "roboto", typo = typoOf({}) }) {
    const dates = lines(person?.dates);
    return (
        <>
            {fioLines(person).map((line, index) => (
                <SpacedLine
                    key={`${index}-${line}`}
                    text={index === 0 && person.surname ? line.toUpperCase() : line}
                    letter={typo.fioLetter * scale}
                    style={lineStyle(fontKey, (index === 0 && person.surname ? typo.surname : typo.name) * scale, { fontFamily, fontWeight: index === 0 && person.surname ? 700 : 400, color, marginBottom: 1 }, typo.fioLine)}
                />
            ))}
            {dates.length ? (
                <div style={{ marginTop: 2 }}>
                    {dates.map((line, index) => (
                        <SpacedLine
                            key={`${index}-${line}`}
                            text={line}
                            letter={typo.datesLetter * scale}
                            style={lineStyle(fontKey, typo.dates * scale, { fontFamily, color }, typo.datesLine)}
                        />
                    ))}
                </div>
            ) : null}
        </>
    );
}

/** Подгрузить шрифт и перерисовать, когда он готов; вернёт CSS-семейство. */
const useStoneFont = (officeId, key) => {
    const [, setTick] = useState(0);
    const family = `${ensureStoneFont(officeId, key)}, Roboto, Arial, sans-serif`;
    useEffect(() => {
        let alive = true;
        ensureStoneFont(officeId, key, () => alive && setTick((value) => value + 1));
        return () => {
            alive = false;
        };
    }, [officeId, key]);
    return family;
};

/** Эпитафия: свой шрифт, размер и интервалы; каждая строка — как в PDF. */
function EpitaphText({ text, fontKey, fontFamily, scale, typo, color, marginTop = 0 }) {
    return (
        <div style={{ marginTop }}>
            {String(text).split("\n").map((line, index) => (
                <SpacedLine
                    key={`${index}-${line}`}
                    text={line || "\u00a0"}
                    letter={typo.epitaphLetter * scale}
                    style={lineStyle(fontKey, typo.epitaph * scale, { fontFamily, fontStyle: italicOf(fontKey), color }, typo.epitaphLine)}
                />
            ))}
        </div>
    );
}

function FrontCell({ officeId, doc, draft }) {
    const blank = draft.blank;
    const front = blank.front || {};
    const image = useSideImage(officeId, doc, front.image);
    const fontKey = front.font || "roboto";
    const fontFamily = useStoneFont(officeId, fontKey);
    const epitaphKey = front.epitaphFont || fontKey;
    const epitaphFamily = useStoneFont(officeId, epitaphKey);
    const fontWidth = STONE_FONT_WIDTH[fontKey] || 1;
    const typo = typoOf(front);
    const count = blank.persons === "triple" ? 3 : blank.persons === "double" ? 2 : 1;
    const sharedSurname = count > 1 && blank.sharedSurnameEnabled ? S(blank.sharedSurname).trim() : "";
    const persons = [draft.deceased || {}, ...(blank.extraPersons || [])].slice(0, count).map((person) => sharedSurname ? { ...person, surname: "" } : person);
    const commonHeading = (scale, color) => sharedSurname ? <PersonText person={{ surname: sharedSurname }} scale={scale} color={color} fontFamily={fontFamily} fontKey={fontKey} typo={typo} /> : null;
    const epitaph = !(blank.backEnabled && blank.epitaphSide === "back") ? S(draft.deceased?.epitaph).trim() : "";
    const top = lines(front.text);
    const maxH = Math.max(120, BOX_H - top.length * 13 - 6);

    const plainText = (scale, color = "#111827") => (
        <div className="blank-sheet__stone-text" style={{ color }}>
            {commonHeading(scale, color)}
            <div className="blank-sheet__persons">
                {persons.map((person, index) => (
                    <div key={index} style={{ flex: 1 }}>
                        <PersonText person={person} scale={scale * (persons.length > 1 ? 0.78 : 1)} color={color} fontFamily={fontFamily} fontKey={fontKey} typo={typo} />
                    </div>
                ))}
            </div>
            {epitaph ? <EpitaphText text={epitaph} fontKey={epitaphKey} fontFamily={epitaphFamily} scale={scale} typo={typo} color={color} marginTop={5} /> : null}
        </div>
    );

    return (
        <div className="blank-sheet__cell-inner" style={{ padding: image.src ? 4 : "10px 6px 6px" }}>
            {top.map((line) => <div key={line} style={{ fontSize: 8.5, textAlign: "center", marginBottom: 2 }}>{line}</div>)}
            {image.src ? (
                <>
                    <CroppedPhoto image={image} side={front} maxW={BOX_W} maxH={maxH}>
                        {({ width, height }) => {
                            if (!front.overlay) return null;
                            const color = front.overlayColor || "#ffffff";
                            const textScale = Math.max(0.3, (Number(front.textScale) || 1) * (height / 250));
                            const textWidth = Math.max(40, Math.round(width * (Number(front.textWidth) || 90) / 100));
                            const blockWidth = persons.length === 1 ? width : textWidth;
                            const personScale = personScaleOf(persons, textScale, blockWidth / persons.length, fontWidth, typo);
                            const shiftX = Math.round(width * Math.min(50, Math.max(-50, Number(front.textX) || 0)) / 100);
                            return (
                                <div
                                    className="blank-sheet__overlay"
                                    style={{ top: height * Math.min(0.95, Math.max(0, (front.textY ?? 38) / 100)), left: (width - blockWidth) / 2 + shiftX, width: blockWidth, color }}
                                >
                                    {commonHeading(textScale, color)}
                                    <div className="blank-sheet__persons">
                                        {persons.map((person, index) => (
                                            <div key={index} style={{ width: blockWidth / persons.length }}>
                                                <PersonText person={person} scale={personScale} color={color} fontFamily={fontFamily} fontKey={fontKey} typo={typo} />
                                            </div>
                                        ))}
                                    </div>
                                    {epitaph ? <EpitaphText text={epitaph} fontKey={epitaphKey} fontFamily={epitaphFamily} scale={textScale} typo={typo} color={color} marginTop={5 * textScale} /> : null}
                                </div>
                            );
                        }}
                    </CroppedPhoto>
                    {/* На автоснимке сцены надпись уже есть — под фото не повторяем. */}
                    {!front.overlay && front.image?.type !== "scene" ? <div style={{ marginTop: 4 }}>{plainText(0.8)}</div> : null}
                </>
            ) : (
                <div style={{ marginTop: 18 }}>{plainText(1)}</div>
            )}
        </div>
    );
}

/** Оборот — как лицевая: фото на всю клетку, текст оборота поверх камня (по умолчанию по центру). */
function BackCell({ officeId, doc, draft }) {
    const blank = draft.blank;
    if (blank.backEnabled === false) return <div className="blank-sheet__cell-inner" />;
    return <BackCellContent officeId={officeId} doc={doc} draft={draft} />;
}

function BackCellContent({ officeId, doc, draft }) {
    const blank = draft.blank;
    const back = blank.back || {};
    const front = blank.front || {};
    const image = useSideImage(officeId, doc, back.image);
    const epitaph = blank.epitaphSide === "back" ? S(draft.deceased?.epitaph).trim() : "";
    const textLines = lines(back.text);
    // Текст оборота — шрифтом эпитафии.
    const fontKey = front.epitaphFont || front.font || "roboto";
    const fontFamily = useStoneFont(officeId, fontKey);
    const fontWidth = STONE_FONT_WIDTH[fontKey] || 1;

    if (!image.src) {
        return (
            <div className="blank-sheet__cell-inner" style={{ padding: "18px 6px 6px", fontFamily }}>
                {textLines.map((line) => <div key={line} style={{ fontSize: 10, textAlign: "center", marginBottom: 3 }}>{line}</div>)}
                {epitaph ? <div style={{ fontSize: 10, fontStyle: "italic", textAlign: "center", marginTop: 6, whiteSpace: "pre-line" }}>{epitaph}</div> : null}
            </div>
        );
    }

    return (
        <div className="blank-sheet__cell-inner" style={{ padding: 4 }}>
            <CroppedPhoto image={image} side={back} maxW={BOX_W} maxH={BOX_H - 6}>
                {({ width, height }) => {
                    if ((!textLines.length && !epitaph) || back.image?.type === "scene") return null;
                    const color = front.overlayColor || "#ffffff";
                    let scale = Math.max(0.3, (Number(back.textScale) || 1) * (height / 250));
                    [...textLines, epitaph].filter(Boolean).forEach((line) => {
                        const longest = Math.max(...line.split("\n").map((part) => part.length));
                        scale = Math.min(scale, (width * 0.8) / Math.max(1, longest * 10 * 0.6 * fontWidth));
                    });
                    const epitaphLines = epitaph ? epitaph.split("\n").length : 0;
                    const blockH = (textLines.length + epitaphLines) * 10 * scale * 1.15 + textLines.length * 2 + (epitaph && textLines.length ? 4 : 0);
                    const ratio = Math.min(0.95, Math.max(0.05, (back.textY ?? 50) / 100));
                    const top = Math.max(0, Math.round(height * ratio - blockH / 2));
                    const shiftX = Math.round(width * Math.min(50, Math.max(-50, Number(back.textX) || 0)) / 100);
                    return (
                        <div className="blank-sheet__overlay" style={{ top, left: shiftX, width, color, fontFamily }}>
                            {textLines.map((line) => <div key={line} style={lineStyle(fontKey, 10 * scale, { fontFamily, marginBottom: 2 })}>{line}</div>)}
                            {epitaph ? <div style={lineStyle(fontKey, 10 * scale, { fontFamily, fontStyle: italicOf(fontKey), marginTop: textLines.length ? 4 : 0, whiteSpace: "pre-line" })}>{epitaph}</div> : null}
                        </div>
                    );
                }}
            </CroppedPhoto>
        </div>
    );
}

function Underline({ label, value }) {
    return (
        <div className="blank-sheet__uline">
            <span>{label}</span>
            <b>{value || " "}</b>
        </div>
    );
}

/** Один лист А4: в пунктах PDF, масштабируется целиком; длиннее А4 — растягивается. */
function PageBox({ scale, children }) {
    const pageRef = useRef(null);
    const [pageH, setPageH] = useState(PAGE_H);
    useLayoutEffect(() => {
        const node = pageRef.current;
        if (!node) return undefined;
        const update = () => setPageH(Math.max(PAGE_H, node.offsetHeight || PAGE_H));
        update();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);
    return (
        <div className="blank-sheet__page-box" style={{ width: PAGE_W * scale, height: pageH * scale }}>
            <div className="blank-sheet__page" ref={pageRef} style={{ width: PAGE_W, minHeight: PAGE_H, padding: `24px ${MARGIN_X}px 30px`, transform: `scale(${scale})` }}>
                {children}
            </div>
        </div>
    );
}

/** Шапка листа — как sheetHeader в PDF. */
function SheetHead({ doc, blank }) {
    return (
        <div className="blank-sheet__head">
            <div>
                <b style={{ fontSize: 10 }}>{doc.office.name || "Мастерская"}</b>
                <div style={{ fontSize: 7, color: MUTED }}>{[doc.office.address, ...(doc.office.phones || []).slice(0, 2), doc.office.site].filter(Boolean).join(" · ")}</div>
            </div>
            <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 6.5, color: MUTED, fontWeight: 700 }}>ПРЕДПРОСМОТР</div>
                <div style={{ fontSize: 9, fontWeight: 700 }}>Приложение к договору № {blank.contractNumber || doc.order.number}</div>
                <div style={{ fontSize: 8.5 }}>от {blank.contractDate || dateText(doc.order.createdAt)}</div>
            </div>
        </div>
    );
}

function CustomerBlock({ customer, blank }) {
    return (
        <div className="blank-sheet__customer">
            <div style={{ width: "58%" }}>
                <Underline label="Ф.И.О. заказчика" value={customer.name} />
                <Underline label="Адрес заказчика" value={customer.address} />
            </div>
            <div style={{ flex: 1 }}>
                <Underline label="Телефон" value={customer.phone} />
                <Underline label="Telegram / MAX" value={blank.messengerPhone} />
            </div>
        </div>
    );
}

const FENCE_LAYOUT_TEXT = { perimeter: "участок (по периметру)", straight: "прямой забор" };

/** Картинка ограды: снимок сцены или фото товара (как в PDF). */
function FenceImage({ officeId, doc, fence, manual }) {
    const ref = manual.shotId ? { type: "shot", id: manual.shotId } : fence.picture ? { type: "product", id: String(fence.n) } : null;
    const image = useSideImage(officeId, doc, ref);
    if (!image.src) return null;
    const fit = Math.min(230 / (image.w || 230), 200 / (image.h || 200));
    return <img src={image.src} alt="" style={{ width: (image.w || 230) * fit, height: (image.h || 200) * fit, flex: "0 0 auto" }} />;
}

/** Лист «Ограда и прочие изделия» — без цен. */
function FencePage({ officeId, doc, draft, customer, scale }) {
    const blank = draft.blank;
    return (
        <PageBox scale={scale}>
            <SheetHead doc={doc} blank={blank} />
            <div className="blank-sheet__title">ОГРАДА И ПРОЧИЕ ИЗДЕЛИЯ — ОПИСАНИЕ РАБОТ</div>
            <CustomerBlock customer={customer} blank={blank} />
            {(doc.fences || []).map((fence, index) => {
                const manual = blank.fences?.[fence.key] || {};
                const size = fence.length && fence.width ? `${fence.length}×${fence.width} мм` : S(fence.size);
                const rowsList = [
                    ["Наименование", `${S(fence.title)}${fence.article ? `  (арт. ${fence.article})` : ""}`],
                    ["Тип", FENCE_LAYOUT_TEXT[fence.layout] || ""],
                    ["Размер", size],
                    ["Высота", fence.height ? `${fence.height} мм` : ""],
                    ["Погонных метров", fence.meters ? `${fence.meters} м` : ""],
                    ["Материал / цвет", [fence.material, fence.color].filter(Boolean).filter((v, i, arr) => arr.indexOf(v) === i).join(", ")],
                    ["Количество", fence.qty ? `${fence.qty} шт` : ""],
                    ["Ножки (столбы)", manual.posts ? `${manual.posts} шт` : ""],
                    ["Калитка", manual.gate],
                    ["Где калитка", manual.gateSide],
                    ["Куда открывается", manual.gateOpen],
                    ["Ширина калитки", manual.gateWidth ? `${manual.gateWidth} мм` : ""],
                ];
                return (
                    <div key={fence.key} style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, margin: "4px 0" }}>{index + 1}. {fence.title || "Ограда"}</div>
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <FenceImage officeId={officeId} doc={doc} fence={fence} manual={manual} />
                            <table className="blank-sheet__kv">
                                <tbody>
                                    {rowsList.map(([label, value]) => (
                                        <tr key={label}><td>{label}</td><td>{value || "\u00a0"}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {manual.note ? <div style={{ fontSize: 9, marginTop: 4 }}><span style={{ color: MUTED }}>Примечание: </span>{manual.note}</div> : null}
                    </div>
                );
            })}
            {(doc.others || []).length ? (
                <>
                    <div style={{ fontSize: 10.5, fontWeight: 700, margin: "4px 0" }}>Прочие изделия</div>
                    <div className="blank-sheet__prices">
                        <table>
                            <tbody>
                                <tr><th>№</th><th>Изделие</th><th>Артикул</th><th>Размер</th><th>Материал / цвет</th><th style={{ textAlign: "center" }}>Кол-во</th></tr>
                                {doc.others.map((item) => (
                                    <tr key={item.n}>
                                        <td>{item.n}</td>
                                        <td>{item.title}{item.notes?.length ? <div style={{ fontSize: 7, color: MUTED }}>{item.notes.join("; ")}</div> : null}</td>
                                        <td>{item.article}</td>
                                        <td>{item.size}</td>
                                        <td>{item.color}</td>
                                        <td style={{ textAlign: "center" }}>{item.qty}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : null}
            <div className="blank-sheet__row is-sign" style={{ marginTop: 14 }}>
                <b style={{ flex: 1, fontSize: 10 }}>С моих слов записано верно, мною проверено</b>
                <div className="blank-sheet__signline"><i />подпись заказчика</div>
            </div>
        </PageBox>
    );
}

const parsePrice = (value) => {
    if (value === undefined || value === null || String(value).trim() === "") return null;
    const number = Number(String(value).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(number) && number >= 0 ? number : null;
};

/**
 * Строки листа цен + ручные цены из бланка (считаем тут же, без запроса к серверу).
 * Старый сервер без priceRows — показываем позиции как раньше, но в один ряд.
 */
const priceRowsWithOverrides = (doc, overrides = {}) => {
    const source = Array.isArray(doc.priceRows) && doc.priceRows.length
        ? doc.priceRows
        : (doc.items || []).map((item) => ({ ...item, key: `n${item.n}`, sub: [item.size, item.color, item.meters].filter(Boolean).join(" · "), editable: false }));
    let manualSum = 0;
    const rows = source.map((row) => {
        if (!row.editable) return row;
        const price = parsePrice(overrides[row.key]);
        if (price === null) return { ...row, price: 0, sum: 0, noPrice: true, manual: false };
        const sum = Math.round(price * (Number(row.qty) || 1) * 100) / 100;
        manualSum += sum;
        return { ...row, price, sum, noPrice: false, manual: true };
    });
    const t = doc.totals || {};
    const hasBase = typeof t.baseTotal === "number";
    const total = hasBase ? Math.round((t.baseTotal + manualSum) * 100) / 100 : t.total;
    const subtotal = hasBase ? Math.round(((t.baseSubtotal ?? t.baseTotal) + manualSum) * 100) / 100 : t.subtotal;
    const prepayment = Number(t.prepayment) || 0;
    return {
        rows,
        totals: { ...t, total, subtotal, remaining: Math.max(0, Math.round((total - prepayment) * 100) / 100) },
    };
};

const priceInputStyle = {
    width: 84,
    padding: "1px 4px",
    border: "1px dashed #b45309",
    borderRadius: 3,
    background: "#fffbeb",
    font: "inherit",
    textAlign: "right",
};

const PriceFolderIcon = () => <svg width="15" height="12" viewBox="0 0 20 16" fill="none" aria-hidden="true" style={{ flex: '0 0 auto' }}><path d="M1 4V2h7l2 2h9v10H1V4Z" fill="#e2e8f0" stroke="#64748b" /><path d="M1 6h18l-2 8H2L1 6Z" fill="#f1f5f9" stroke="#64748b" /></svg>;

/** Последний лист — открытое дерево памятника, комплекта и оформления. */
function PricePage({ doc, draft, customer, scale, onBlank, highlightUnpriced = false }) {
    const blank = draft.blank;
    const overrides = blank.priceOverrides || {};
    const { rows, totals } = priceRowsWithOverrides(doc, overrides);
    const setOverride = (row, value) => {
        if (!onBlank) return;
        const key = row.key;
        onBlank((prev) => {
            const next = { ...(prev.priceOverrides || {}) };
            const meta = { ...(prev.priceOverrideRows || {}) };
            if (String(value).trim() === "") {
                delete next[key];
                delete meta[key];
            } else {
                next[key] = String(value).slice(0, 14);
                meta[key] = { title: String(row.title || ""), qty: Number(row.qty) || 1, sceneKey: String(row.sceneKey || "") };
            }
            return { ...prev, priceOverrides: next, priceOverrideRows: meta };
        });
    };
    const totalLines = [
        totals.discount > 0 ? ["Без скидки", money(totals.subtotal)] : null,
        totals.discount > 0 ? ["Скидка", `− ${money(totals.discount)}`] : null,
        ["Итого", money(totals.total)],
        totals.prepayment > 0 ? ["Предоплата", money(totals.prepayment)] : null,
        totals.prepayment > 0 ? ["Остаток", money(totals.remaining)] : null,
    ].filter(Boolean);
    return (
        <PageBox scale={scale}>
            <SheetHead doc={doc} blank={blank} />
            <div className="blank-sheet__title">СПИСОК ПОЗИЦИЙ И СТОИМОСТЬ</div>
            <CustomerBlock customer={customer} blank={blank} />
            <div className="blank-sheet__prices">
                <table>
                    <tbody>
                        <tr><th>№</th><th>Позиция</th><th>Артикул</th><th style={{ textAlign: "center" }}>Кол-во</th><th style={{ textAlign: "right" }}>Цена</th><th style={{ textAlign: "right" }}>Сумма</th></tr>
                        {rows.map((row) => (
                            <React.Fragment key={row.key || row.n}>
                            {row.treeStart ? <tr><td colSpan={6} style={{ background: '#eef2f7', padding: '8px 6px', borderTop: '1px solid #cbd5e1' }}><span style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 700 }}><PriceFolderIcon />{row.treeTitle}</span></td></tr> : null}
                            {row.ledgerStart ? <tr><td colSpan={6} style={{ padding: '6px 6px 4px 20px' }}><span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}><PriceFolderIcon />Надгробная плита</span></td></tr> : null}
                            <tr>
                                <td>{row.n}</td>
                                <td style={{ paddingLeft: row.treeDepth ? 8 + row.treeDepth * 14 : undefined, fontWeight: row.treeFolder ? 700 : undefined }}>
                                    {row.treeFolder ? <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 5 }}><PriceFolderIcon /></span> : null}{row.title}
                                    {row.sub ? <span style={{ color: MUTED }}>{` · ${row.sub}`}</span> : null}
                                    {row.priceConfirmed === false ? <span style={{ color: "#b45309" }}> · цена уточняется</span> : null}
                                </td>
                                <td>{row.article}</td>
                                <td style={{ textAlign: "center" }}>{row.qtyText || row.qty}</td>
                                {row.included ? (
                                    <td colSpan={2} style={{ textAlign: "right", color: MUTED }} title={row.coveredBy ? `Входит в работу ${row.coveredBy}` : undefined}>{row.includedLabel || "в комплекте"}</td>
                                ) : row.editable && onBlank ? (
                                    <>
                                        <td style={{ textAlign: "right" }}>
                                            <input
                                                inputMode="decimal"
                                                aria-invalid={highlightUnpriced && row.noPrice}
                                                style={highlightUnpriced && row.noPrice ? { ...priceInputStyle, border: "2px solid #dc2626", background: "#fef2f2" } : priceInputStyle}
                                                value={overrides[row.key] ?? ""}
                                                placeholder="цена, ₽"
                                                title="Цены нет в прайсе — впишите вручную, попадёт в итог и PDF"
                                                onChange={(event) => setOverride(row, event.target.value.replace(/[^\d.,\s]/g, ""))}
                                            />
                                        </td>
                                        <td style={{ textAlign: "right", color: row.manual ? undefined : "#b45309" }}>{row.manual ? money(row.sum) : "—"}</td>
                                    </>
                                ) : row.noPrice ? (
                                    <td colSpan={2} style={{ textAlign: "right", color: "#b45309" }}>цена не указана</td>
                                ) : (
                                    <>
                                        <td style={{ textAlign: "right" }}>{money(row.price)}</td>
                                        <td style={{ textAlign: "right" }}>{money(row.sum)}</td>
                                    </>
                                )}
                            </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="blank-sheet__total-box">
                {totalLines.map(([label, value]) => (
                    <div key={label} className={label === "Итого" || label === "Остаток" ? "is-bold" : ""}><span>{label}</span><span>{value}</span></div>
                ))}
            </div>
            <div className="blank-sheet__price-signs">
                <div><small>Заказчик</small><i /></div>
                <div><small>Исполнитель{draft.acceptedBy || doc.acceptedBy ? ` (${draft.acceptedBy || doc.acceptedBy})` : ""}</small><i /></div>
            </div>
        </PageBox>
    );
}

export default function BlankSheetPreview({ officeId, doc, draft, onBlank, highlightUnpriced = false }) {
    const wrapRef = useRef(null);
    const [wrapWidth, setWrapWidth] = useState(600);
    const [zoom, setZoom] = useState(1);

    useLayoutEffect(() => {
        const node = wrapRef.current;
        if (!node) return undefined;
        const update = () => setWrapWidth(node.clientWidth || 600);
        update();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const blank = draft.blank;
    const layout = { ...(doc.layout || {}), ...(draft.layout || {}) };
    const customer = {
        name: draft.overrides.customerName || doc.customer.name,
        phone: draft.overrides.customerPhone || doc.customer.phone,
        address: draft.overrides.customerAddress || doc.customer.address,
    };
    const object = {
        cemetery: draft.overrides.cemeteryName || doc.object.cemetery,
        sector: draft.overrides.sector || doc.object.sector,
        plot: draft.overrides.plot || doc.object.plot,
    };
    const terms = lines(draft.termsText || doc.terms);
    const warningIndex = terms.findIndex((line) => /стел/i.test(line) && /запрещ|трогать/i.test(line));
    const warning = warningIndex >= 0 ? terms[warningIndex] : "";
    const otherTerms = terms.filter((_, index) => index !== warningIndex);
    const comment = draft.overrides.comment || doc.comment;
    const noInstall = blank.installWorkMode === "none";
    const onlyDismantle = blank.installWorkMode === "dismantle";
    const place = blank.installPlace || [object.cemetery, object.sector && `сектор ${object.sector}`, object.plot && `участок ${object.plot}`].filter(Boolean).join(", ");
    const components = blank.components || [];
    const rows = Math.max(components.length, CHECK_LABELS.length);
    const options = OPTION_LABELS.filter(([key]) => S(blank.options?.[key]).trim());
    const extras = (blank.extras || []).filter((line) => S(line).trim());

    const fitScale = Math.max(0.2, (wrapWidth - 24) / PAGE_W);
    const scale = fitScale * zoom;

    return (
        <div className="blank-sheet">
            <div className="blank-sheet__bar">
                <button type="button" onClick={() => setZoom((value) => Math.max(0.5, Math.round((value - 0.15) * 100) / 100))} title="Мельче">−</button>
                <button type="button" onClick={() => setZoom(1)} title="Весь лист по ширине">{Math.round(zoom * 100)}%</button>
                <button type="button" onClick={() => setZoom((value) => Math.min(3, Math.round((value + 0.15) * 100) / 100))} title="Крупнее">+</button>
            </div>
            <div className="blank-sheet__scroll" ref={wrapRef}>
                <PageBox scale={scale}>
                        <SheetHead doc={doc} blank={blank} />

                        <div className="blank-sheet__title">ПОДРОБНОЕ ОПИСАНИЕ РАБОТ, ПОДЛЕЖАЩИХ ИСПОЛНЕНИЮ ПО ДОГОВОРУ</div>

                        <CustomerBlock customer={customer} blank={blank} />

                        {layout.showDeceasedSection !== false ? (
                            <div className="blank-sheet__sketch">
                                <div className="blank-sheet__sketch-head">
                                    <span>ЛИЦЕВАЯ СТОРОНА</span>
                                    <span>{S(blank.back?.title || "Оборотная сторона").toUpperCase()}</span>
                                </div>
                                <div className="blank-sheet__sketch-body">
                                    <div><FrontCell officeId={officeId} doc={doc} draft={draft} /></div>
                                    <div><BackCell officeId={officeId} doc={doc} draft={draft} /></div>
                                </div>
                            </div>
                        ) : null}

                        {layout.showOrderDetails !== false ? (
                            <>
                                <div style={{ fontSize: 10.5, fontWeight: 700, margin: "2px 0 3px" }}>
                                    Наименование стелы: {blank.title || "—"}
                                    {blank.article ? <span style={{ fontSize: 9, fontWeight: 400, color: MUTED }}>{`   арт. ${blank.article}`}</span> : null}
                                </div>
                                <table className="blank-sheet__table">
                                    <colgroup>
                                        <col style={{ width: 88 }} />
                                        <col />
                                        <col style={{ width: 52 }} />
                                        <col style={{ width: 150 }} />
                                        <col style={{ width: 16 }} />
                                    </colgroup>
                                    <tbody>
                                        <tr><td>наименование</td><td>Размеры</td><td>полировка</td><td>Примечания (отметить галочкой)</td><td /></tr>
                                        {Array.from({ length: rows }).map((_, index) => {
                                            const row = components[index] || {};
                                            const check = CHECK_LABELS[index];
                                            return (
                                                <tr key={index}>
                                                    <td style={{ fontWeight: 700 }}>{row.name}</td>
                                                    <td style={{ fontWeight: 700, fontSize: 9.5 }}>{row.size}{row.note ? <span style={{ fontWeight: 400, fontSize: 7.5, color: MUTED }}>{`  ${row.note}`}</span> : null}</td>
                                                    <td style={{ textAlign: "center" }}>{row.polish}</td>
                                                    <td style={{ fontWeight: check ? 700 : 400 }}>{check ? check[1] : ""}</td>
                                                    <td style={{ textAlign: "center", fontWeight: 700 }}>{check && blank.checks?.[check[0]] ? "+" : ""}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {options.length ? (
                                    <div className="blank-sheet__options">
                                        {options.map(([key, label]) => (
                                            <div key={key}><span style={{ color: MUTED }}>{label}: </span><b>{blank.options[key]}</b></div>
                                        ))}
                                    </div>
                                ) : null}
                                {extras.length ? (
                                    <ol className="blank-sheet__extras">{extras.map((line, index) => <li key={`${index}-${line}`}>{line}</li>)}</ol>
                                ) : null}
                            </>
                        ) : null}

                        <div className="blank-sheet__dates">
                            {layout.showDatesSection !== false && !noInstall && !onlyDismantle ? (
                                <div>Месяц и год установки: <b>{blank.installPeriod || monthYear(draft.installationAt || doc.installationAt) || "установочный период"}</b></div>
                            ) : null}
                            {layout.showDatesSection !== false && !noInstall && blank.showMonth ? <div>Плановый месяц показа: <b>{blank.showMonth}</b></div> : null}
                            {layout.showGeoSection !== false && (place || blank.deliveryPlace) ? (
                                <div>
                                    {!noInstall && place ? <>{onlyDismantle ? "Место демонтажа: " : "Место установки: "} <b>{place}</b></> : null}
                                    {blank.deliveryPlace ? <>{!noInstall && place ? ";  " : ""}место доставки — <b>{blank.deliveryPlace}</b></> : null}
                                </div>
                            ) : null}
                            {S(draft.deceased?.epitaph).trim() ? (
                                <div>
                                    Эпитафия: <b>{epitaphChars(draft.deceased.epitaph).letters} знаков без пробелов</b>
                                    <span style={{ color: MUTED }}> (всего {epitaphChars(draft.deceased.epitaph).total})</span>
                                </div>
                            ) : null}
                            {comment ? <div style={{ fontSize: 9 }}><span style={{ color: MUTED }}>Примечание: </span>{comment}</div> : null}
                        </div>

                        {layout.showSignatures !== false ? (
                            <div className="blank-sheet__signs">
                                <div className="blank-sheet__row">
                                    <span>Заказ принял</span>
                                    <b style={{ flex: 1, marginLeft: 6 }}>{draft.acceptedBy || doc.acceptedBy || "______________________"}</b>
                                    <small>(фамилия приемщика)</small>
                                </div>
                                {layout.showTerms !== false && otherTerms.length ? (
                                    <ul className="blank-sheet__terms">{otherTerms.map((line) => <li key={line}>{line}</li>)}</ul>
                                ) : null}
                                <div className="blank-sheet__row is-sign">
                                    <b style={{ flex: 1, fontSize: 10 }}>С моих слов записано верно, мною проверено</b>
                                    <div className="blank-sheet__signline"><i />подпись заказчика, расшифровка</div>
                                </div>
                                {layout.showTerms !== false && warning ? (
                                    <div className="blank-sheet__row is-sign">
                                        <b style={{ flex: 1, fontSize: 9.5 }}>{warning}</b>
                                        <div className="blank-sheet__signline"><i />подпись заказчика</div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                </PageBox>
                {doc.ledger ? <PageBox scale={scale}>
                    <SheetHead doc={doc} blank={blank} />
                    <div className="blank-sheet__title">НАДГРОБНАЯ ПЛИТА — ВИД СВЕРХУ И ОФОРМЛЕНИЕ</div>
                    <CustomerBlock customer={customer} blank={blank} />
                    <p style={{ fontSize: 11, fontWeight: 700 }}>Размеры плиты: {doc.ledger.width} × {doc.ledger.length} × {doc.ledger.thickness} мм (ширина × длина × толщина)</p>
                    <p style={{ fontSize: 10 }}>Полировка: 4 ст — верх, передний торец и два бока. Торец у подставки и низ — неполированные.</p>
                    {blank.showPrices && doc.ledger.slabPrice ? (() => {
                        const price = priceRowsWithOverrides(doc, draft.blank.priceOverrides || {}).rows.find((row) => row.key === doc.ledger.slabPrice.key) || doc.ledger.slabPrice;
                        return <p style={{ fontSize: 11 }}>Стоимость плиты: {price.included ? 'в комплекте' : price.noPrice ? 'не указана' : `${Number(price.sum || 0).toLocaleString('ru-RU')} ₽`}</p>;
                    })() : null}
                    {doc.sceneShots?.ledger?.src ? <img src={doc.sceneShots.ledger.src} alt="Надгробная плита — вид сверху" style={{ display: 'block', width: '100%', height: 400, objectFit: 'contain', marginBottom: 12 }} /> : <p>Снимок надгробной плиты не приложен.</p>}
                    {doc.ledger.decorations.map((entry) => {
                        const liveRows = priceRowsWithOverrides(doc, draft.blank.priceOverrides || {}).rows.filter((row) => (entry.priceRowKeys || []).includes(row.key));
                        const price = liveRows.length ? { sum: liveRows.reduce((sum, row) => sum + row.sum, 0), noPrice: liveRows.some((row) => row.noPrice), included: liveRows.every((row) => row.included) } : entry;
                        return <div key={entry.slotKey} style={{ fontSize: 11, margin: '12px 0' }}>
                            <b>{entry.type === 'epitaph' ? 'Эпитафия на надгробной плите' : 'Рисунок на надгробной плите'}</b>
                            <div style={{ whiteSpace: 'pre-wrap', margin: '5px 0' }}>{entry.type === 'epitaph' ? entry.text : entry.title}</div>
                            <div>Размер оформления: {entry.width} × {entry.height} мм{entry.fontSize ? `; высота букв: ${entry.fontSize} мм` : ''}</div>
                            {blank.showPrices ? <b>{price.included ? 'Стоимость: в комплекте' : price.noPrice ? 'Стоимость: не указана' : `Стоимость: ${Number(price.sum || 0).toLocaleString('ru-RU')} ₽`}</b> : null}
                        </div>;
                    })}
                </PageBox> : null}
                {(doc.fences || []).length || (doc.others || []).length ? (
                    <FencePage officeId={officeId} doc={doc} draft={draft} customer={customer} scale={scale} />
                ) : null}
                {blank.showPrices && doc.items?.length ? <PricePage doc={doc} draft={draft} customer={customer} scale={scale} onBlank={onBlank} highlightUnpriced={highlightUnpriced} /> : null}
            </div>
        </div>
    );
}
